import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { CheckCircle2, XCircle, Clock, AlertTriangle } from "lucide-react";

const STATUS_TABS = [
  { key: "applied", label: "Applied" },
  { key: "under_review", label: "Under review" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "suspended", label: "Suspended" },
];

export default function AdminCentre() {
  useSeo({ title: "Admin · Learning Centre | Teacher4Me.ca" });

  const [dashboard, setDashboard] = useState(null);
  const [status, setStatus] = useState("applied");
  const [teachers, setTeachers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [students, setStudents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadStatus, setLeadStatus] = useState("");
  const [view, setView] = useState("teachers"); // teachers | students | leads

  const loadDashboard = async () => {
    try {
      const { data } = await api.get("/api/admin/centre/dashboard");
      setDashboard(data);
    } catch (err) { toast.error("Dashboard load failed"); }
  };

  const loadTeachers = async () => {
    try {
      const { data } = await api.get(`/api/admin/centre/teachers?status=${status}`);
      setTeachers(data.items || []);
    } catch (err) { toast.error("Teachers load failed"); }
  };

  const loadStudents = async () => {
    try {
      const { data } = await api.get("/api/admin/centre/students");
      setStudents(data.items || []);
    } catch (err) { toast.error("Students load failed"); }
  };

  const loadLeads = async () => {
    try {
      const q = leadStatus ? `?status=${leadStatus}` : "";
      const { data } = await api.get(`/api/admin/centre/leads${q}`);
      setLeads(data.items || []);
    } catch (err) { toast.error("Leads load failed"); }
  };

  const updateLead = async (id, patch) => {
    try {
      await api.patch(`/api/admin/centre/leads/${id}`, patch);
      toast.success("Lead updated");
      await loadLeads();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
  };

  const loadDetail = async (uid) => {
    setDetail(null); setSelected(uid);
    try {
      const { data } = await api.get(`/api/admin/centre/teachers/${uid}`);
      setDetail(data);
    } catch (err) { toast.error("Detail load failed"); }
  };

  useEffect(() => { loadDashboard(); }, []);
  useEffect(() => { if (view === "teachers") loadTeachers(); }, [status, view]);
  useEffect(() => { if (view === "students") loadStudents(); }, [view]);
  useEffect(() => { if (view === "leads") loadLeads(); }, [view, leadStatus]);

  const act = async (uid, action, reason) => {
    setBusy(true);
    try {
      const body = reason ? { reason } : {};
      await api.post(`/api/admin/centre/teachers/${uid}/${action}`, body);
      toast.success(`Teacher ${action}`);
      await loadTeachers(); await loadDashboard();
      if (selected === uid) await loadDetail(uid);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadAgreement = async (agreementId) => {
    try {
      const r = await api.get(`/api/centre/agreements/${agreementId}/pdf`,
                                 { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (err) { toast.error("PDF download failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="admin-centre-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="label-eyebrow">Admin</div>
          <h1 className="font-display font-black text-3xl mt-1">Learning Centre</h1>
        </div>
      </div>

      {/* stat tiles */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="centre-stats">
          <Stat label="Teachers applied" value={dashboard.teachers.applied} intent="warn" />
          <Stat label="Teachers approved" value={dashboard.teachers.approved} intent="ok" />
          <Stat label="Students active" value={dashboard.students_active} />
          <Stat label="Signed agreements" value={dashboard.agreements_total} />
        </div>
      )}

      {/* view switch */}
      <div className="flex gap-2 mb-4">
        <ViewTab label="Teachers" active={view === "teachers"} onClick={() => setView("teachers")} testid="tab-teachers" />
        <ViewTab label="Students" active={view === "students"} onClick={() => setView("students")} testid="tab-students" />
        <ViewTab label="Assessment Leads" active={view === "leads"} onClick={() => setView("leads")} testid="tab-leads" />
      </div>

      {view === "teachers" && (
        <>
          <div className="flex flex-wrap gap-2 mb-4">
            {STATUS_TABS.map((t) => (
              <button key={t.key} onClick={() => setStatus(t.key)}
                data-testid={`teacher-status-${t.key}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  status === t.key
                    ? "bg-[#D92D20] text-white border-[#D92D20]"
                    : "bg-white text-[#111827] border-[#E5E7EB] hover:border-[#D92D20]"
                }`}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            <div className="md:col-span-2">
              <div className="bg-white border border-[#E5E7EB] rounded-2xl divide-y">
                {teachers.length === 0 && (
                  <div className="p-6 text-sm text-[#4B5563]" data-testid="empty-teachers">
                    No teachers in this status.
                  </div>
                )}
                {teachers.map((t) => (
                  <button key={t.user_id} onClick={() => loadDetail(t.user_id)}
                    data-testid={`teacher-row-${t.user_id.slice(0, 8)}`}
                    className={`w-full text-left px-4 py-3 hover:bg-[#F9FAFB] transition-colors ${
                      selected === t.user_id ? "bg-[#F9FAFB]" : ""
                    }`}>
                    <div className="font-semibold text-sm">{t.full_name}</div>
                    <div className="text-xs text-[#4B5563]">{t.email}</div>
                    <div className="text-xs mt-1">
                      <span className="text-[#064E3B]">{(t.subjects || []).slice(0, 3).join(", ")}</span>
                      {t.years_exp ? <span className="text-[#4B5563]"> · {t.years_exp} yrs</span> : null}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="md:col-span-3">
              {!detail && (
                <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 text-sm text-[#4B5563]">
                  Select a teacher from the list to review their application.
                </div>
              )}
              {detail && (
                <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 space-y-6" data-testid="teacher-detail">
                  <div>
                    <div className="text-xs uppercase font-semibold text-[#4B5563] tracking-wider">
                      {detail.teacher.status}
                    </div>
                    <div className="font-display font-black text-xl mt-1">{detail.teacher.full_name}</div>
                    <div className="text-sm text-[#4B5563]">{detail.user.email} · {detail.teacher.phone}</div>
                  </div>

                  <Grid cols={2}>
                    <Meta label="Highest education" value={detail.teacher.highest_edu} />
                    <Meta label="Years experience" value={detail.teacher.years_exp} />
                    <Meta label="Subjects" value={(detail.teacher.subjects || []).join(", ")} />
                    <Meta label="Grade levels" value={(detail.teacher.grade_levels || []).join(", ")} />
                    <Meta label="Curriculum" value={(detail.teacher.curriculum_knowledge || []).join(", ")} />
                    <Meta label="Mode" value={detail.teacher.mode} />
                    <Meta label="Available days" value={(detail.teacher.availability_days || []).join(", ")} />
                    <Meta label="Preferred hours" value={detail.teacher.preferred_hours} />
                  </Grid>

                  {detail.teacher.curriculum_desc && (
                    <div>
                      <div className="text-xs uppercase font-semibold text-[#4B5563]">
                        Curriculum notes
                      </div>
                      <p className="text-sm mt-1">{detail.teacher.curriculum_desc}</p>
                    </div>
                  )}

                  {detail.teacher.docs && Object.keys(detail.teacher.docs).length > 0 && (
                    <div>
                      <div className="text-xs uppercase font-semibold text-[#4B5563]">Documents</div>
                      <ul className="text-sm mt-2 space-y-1">
                        {Object.entries(detail.teacher.docs).map(([k, v]) =>
                          v ? (
                            <li key={k}>
                              <span className="text-[#4B5563]">{k}:</span>{" "}
                              <a href={v} target="_blank" rel="noopener noreferrer"
                                className="text-[#D92D20] font-semibold">Open</a>
                            </li>
                          ) : null
                        )}
                      </ul>
                    </div>
                  )}

                  <div>
                    <div className="text-xs uppercase font-semibold text-[#4B5563]">Signed agreements</div>
                    <ul className="text-sm mt-2 space-y-2">
                      {(detail.agreements || []).map((a) => (
                        <li key={a.id} className="flex items-center gap-3">
                          <span>
                            {a.kind} · v{a.version} · {a.signed_at.slice(0, 10)}
                          </span>
                          <button onClick={() => downloadAgreement(a.id)}
                            data-testid={`download-agreement-${a.id.slice(0, 8)}`}
                            className="text-xs text-[#D92D20] font-semibold hover:underline">
                            Download PDF
                          </button>
                        </li>
                      ))}
                      {(!detail.agreements || detail.agreements.length === 0) && (
                        <li className="text-[#4B5563]">No agreements on file.</li>
                      )}
                    </ul>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E5E7EB]">
                    <button disabled={busy || detail.teacher.status === "approved"}
                      onClick={() => act(detail.teacher.user_id, "approve")}
                      data-testid="btn-approve-teacher"
                      className="btn-primary text-sm">Approve</button>
                    <button disabled={busy}
                      onClick={() => act(detail.teacher.user_id, "under-review")}
                      data-testid="btn-review-teacher"
                      className="btn-outline text-sm">Under review</button>
                    <button disabled={busy}
                      onClick={() => {
                        const r = window.prompt("Rejection reason (visible to admin only):");
                        if (r != null) act(detail.teacher.user_id, "reject", r);
                      }}
                      data-testid="btn-reject-teacher"
                      className="text-sm px-4 py-2 border border-[#E5E7EB] rounded-md text-[#B91C1C] hover:bg-[#FEF2F2]">Reject</button>
                    <button disabled={busy}
                      onClick={() => {
                        const r = window.prompt("Suspension reason:");
                        if (r != null) act(detail.teacher.user_id, "suspend", r);
                      }}
                      data-testid="btn-suspend-teacher"
                      className="text-sm px-4 py-2 border border-[#E5E7EB] rounded-md text-[#B91C1C] hover:bg-[#FEF2F2]">Suspend</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {view === "students" && (
        <div className="bg-white border border-[#E5E7EB] rounded-2xl divide-y">
          {students.length === 0 && (
            <div className="p-6 text-sm text-[#4B5563]" data-testid="empty-students">
              No students enrolled yet.
            </div>
          )}
          {students.map((s) => (
            <div key={s.id} className="px-4 py-3 grid grid-cols-4 gap-4 items-center"
              data-testid={`student-row-${s.id.slice(0, 8)}`}>
              <div>
                <div className="font-semibold text-sm">{s.full_name}</div>
                <div className="text-xs text-[#4B5563]">{s.grade} · {s.province}</div>
              </div>
              <div className="text-sm">
                <div className="text-xs text-[#4B5563]">Parent</div>
                <div>{s.parent?.full_name || "—"}</div>
                <div className="text-xs text-[#4B5563]">{s.parent?.email}</div>
              </div>
              <div className="text-sm">
                <div className="text-xs text-[#4B5563]">Help subjects</div>
                <div>{(s.help_subjects || []).join(", ") || "—"}</div>
              </div>
              <div className="text-sm">
                <div className="text-xs text-[#4B5563]">Mode / status</div>
                <div>{s.preferred_mode} · {s.status}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {view === "leads" && (
        <div data-testid="leads-view">
          <div className="flex flex-wrap gap-2 mb-4">
            {["", "new", "contacted", "assessment_booked", "enrolled", "closed"].map((s) => (
              <button key={s || "all"} onClick={() => setLeadStatus(s)}
                data-testid={`lead-status-${s || "all"}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  leadStatus === s
                    ? "bg-[#D92D20] text-white border-[#D92D20]"
                    : "bg-white text-[#111827] border-[#E5E7EB] hover:border-[#D92D20]"
                }`}>
                {s ? s.replace("_", " ") : "All"}
              </button>
            ))}
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-2xl divide-y">
            {leads.length === 0 && (
              <div className="p-6 text-sm text-[#4B5563]" data-testid="empty-leads">
                No leads yet. New submissions from /free-assessment appear here.
              </div>
            )}
            {leads.map((l) => (
              <div key={l.id} className="p-4" data-testid={`lead-row-${l.id.slice(0, 8)}`}>
                <div className="grid md:grid-cols-4 gap-4 items-start">
                  <div>
                    <div className="font-semibold text-sm">{l.student_name}
                      <span className="text-[#4B5563] font-normal"> · {l.grade}</span>
                    </div>
                    <div className="text-xs text-[#4B5563] mt-0.5">
                      Parent: {l.parent_name}
                    </div>
                    <div className="text-xs text-[#4B5563]">
                      School: {l.school_name || "—"}
                    </div>
                  </div>
                  <div className="text-xs space-y-0.5">
                    <div><a href={`tel:${l.phone}`} className="font-semibold text-[#111827]">{l.phone}</a></div>
                    <div><a href={`mailto:${l.email}`} className="text-[#D92D20]">{l.email}</a></div>
                    <div className="text-[#4B5563]">Prefers: {l.preferred_contact}</div>
                    <div className="text-[#4B5563]">Source: {l.source}</div>
                  </div>
                  <div className="text-xs">
                    <div className="font-semibold uppercase text-[#4B5563] tracking-wider mb-0.5">Subject</div>
                    <div className="text-sm">{l.subject}</div>
                    {l.concern && (
                      <>
                        <div className="font-semibold uppercase text-[#4B5563] tracking-wider mt-2 mb-0.5">Concern</div>
                        <div className="text-sm text-[#111827]">{l.concern}</div>
                      </>
                    )}
                  </div>
                  <div className="space-y-2">
                    <select value={l.status}
                      data-testid={`lead-status-select-${l.id.slice(0, 8)}`}
                      onChange={(e) => updateLead(l.id, { status: e.target.value })}
                      className="w-full border border-[#E5E7EB] rounded-md px-2 py-1.5 text-xs bg-white">
                      <option value="new">new</option>
                      <option value="contacted">contacted</option>
                      <option value="assessment_booked">assessment_booked</option>
                      <option value="enrolled">enrolled</option>
                      <option value="closed">closed</option>
                    </select>
                    <textarea
                      defaultValue={l.admin_notes || ""}
                      data-testid={`lead-notes-${l.id.slice(0, 8)}`}
                      rows={2}
                      placeholder="Admin notes…"
                      onBlur={(e) => {
                        if ((e.target.value || "") !== (l.admin_notes || "")) {
                          updateLead(l.id, { admin_notes: e.target.value });
                        }
                      }}
                      className="w-full border border-[#E5E7EB] rounded-md px-2 py-1.5 text-xs" />
                    <div className="text-[10px] text-[#4B5563]">
                      {new Date(l.created_at).toLocaleString("en-CA")}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, intent }) {
  const bg = intent === "ok" ? "bg-[#ECFDF5] border-[#A7F3D0]"
        : intent === "warn" ? "bg-[#FFFBEB] border-[#FDE68A]"
        : "bg-white border-[#E5E7EB]";
  const icon = intent === "ok" ? <CheckCircle2 className="w-4 h-4 text-[#065F46]" />
        : intent === "warn" ? <Clock className="w-4 h-4 text-[#92400E]" />
        : null;
  return (
    <div className={`rounded-xl border p-4 ${bg}`}>
      <div className="text-xs text-[#4B5563] font-semibold uppercase tracking-wider flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className="text-3xl font-black mt-1">{value ?? 0}</div>
    </div>
  );
}

function ViewTab({ label, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 rounded-md text-sm font-semibold border ${
        active ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-[#111827] border-[#E5E7EB]"
      }`}>
      {label}
    </button>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase font-semibold text-[#4B5563] tracking-wider">{label}</div>
      <div className="text-sm">{value || "—"}</div>
    </div>
  );
}

function Grid({ cols, children }) {
  return <div className={`grid gap-4 ${cols === 2 ? "sm:grid-cols-2" : ""}`}>{children}</div>;
}
